import React from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { SafeDatePicker } from "../../fields/SafeDatePicker";
import { SectionAccordion } from "./SectionAccordion";
import GavelIcon from "@mui/icons-material/Gavel";
import moment from "moment";
import {
  ForwardCommitmentSectionProps,
  ChangeCountForwardCommitmentSectionProps,
} from "../../../../types";

export interface ForwardCommitmentSectionComponentProps {
  state: ForwardCommitmentSectionProps;
  setState: (state: ForwardCommitmentSectionProps) => void;
  mode: "edit" | "review";
  minDate: any;
  pageVariant: "create" | "edit" | "view";
  alwaysShowDateFields?: boolean;
  initialState?: ForwardCommitmentSectionProps;
  changeCount?: ChangeCountForwardCommitmentSectionProps;
  forceMobile?: boolean;
}

export function ForwardCommitmentSection({
  state,
  setState,
  mode,
  minDate,
  pageVariant,
  alwaysShowDateFields = false,
  initialState,
  changeCount,
  forceMobile,
}: ForwardCommitmentSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  const showDateFields = alwaysShowDateFields || state.HasForwardCommitment;

  return (
    <SectionAccordion
      id="forwardCommitment"
      title="Forward Commitment"
      icon={<GavelIcon fontSize="large" sx={{ color: "#ACACAC" }} />}
      total={0}
      forceMobile={forceMobile}
    >
      {/* Has Forward Commitment? */}
      <Grid item xs={labelXs} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ textAlign: "start", mt: 2 }}>
          Has Forward Commitment?
        </Typography>
        <FormControl>
          <FormLabel sx={{ textAlign: "start", mt: 1, ml: 1 }}></FormLabel>
        </FormControl>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body1" fontWeight="normal" sx={{ textAlign: "start", mt: 2, ml: 1 }}>
            {initialState?.HasForwardCommitment === undefined ? "" : initialState.HasForwardCommitment ? "Yes" : "No"}{" "}
            {(changeCount?.HasForwardCommitment ?? 0) > 1 ? `(${changeCount?.HasForwardCommitment})` : ""}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        <FormControl>
          <FormLabel sx={{ textAlign: "start", mt: 1, ml: 1 }}></FormLabel>
          <RadioGroup
            row
            value={state.HasForwardCommitment ? "true" : "false"}
            onChange={(e) => {
              if (mode !== "review" && !isView) {
                setState({
                  ...state,
                  HasForwardCommitment: e.target.value === "true",
                  ForwardCommitmentBatchNumber: e.target.value === "true" ? state.ForwardCommitmentBatchNumber : undefined,
                  ForwardCommitmentExpiryDate: e.target.value === "true" ? moment(state.ForwardCommitmentExpiryDate).startOf("day") : undefined,
                });
              }
            }}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" disabled={mode === "review" || isView} />
            <FormControlLabel value="false" control={<Radio />} label="No" disabled={mode === "review" || isView} />
          </RadioGroup>
        </FormControl>
      </Grid>

      {showDateFields && (
        <>
          {/* Expiry Date */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Expiry Date
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body1" fontWeight="normal" sx={{ alignSelf: "center" }}>
                {initialState?.ForwardCommitmentExpiryDate === undefined ? "" : moment(initialState.ForwardCommitmentExpiryDate).format("MM/DD/YYYY")}{" "}
                {(changeCount?.ForwardCommitmentExpiryDate ?? 0) > 1 ? `(${changeCount?.ForwardCommitmentExpiryDate})` : ""}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            {alwaysShowDateFields || isView ? (
              <DatePicker
                minDate={minDate}
                disabled
                value={state.ForwardCommitmentExpiryDate}
                onChange={(newValue: any) => {
                  if ((newValue && !newValue.isValid()) || (newValue && newValue.isBefore(moment(), "day"))) {
                    setState({ ...state, ForwardCommitmentExpiryDate: moment().startOf("day") });
                  } else {
                    setState({ ...state, ForwardCommitmentExpiryDate: moment(newValue).startOf("day") });
                  }
                }}
                label="Expiry Date"
                sx={{ width: "100%" }}
              />
            ) : (
              <SafeDatePicker
                label="Expiry Date"
                value={state.ForwardCommitmentExpiryDate ?? null}
                onChange={(v) => setState({ ...state, ForwardCommitmentExpiryDate: v })}
                minDate={minDate}
                disabled={mode === "review"}
              />
            )}
          </Grid>

          {/* Batch Number */}
          <Grid item xs={labelXs} display="flex">
            <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
              Batch Number
            </Typography>
          </Grid>
          {isEditView && !isMobile && (
            <Grid item xs={fieldXs}>
              <Typography variant="body1" fontWeight="normal" sx={{ alignSelf: "center" }}>
                {initialState?.ForwardCommitmentBatchNumber === undefined ? "" : initialState.ForwardCommitmentBatchNumber}{" "}
                {(changeCount?.ForwardCommitmentBatchNumber ?? 0) > 1 ? `(${changeCount?.ForwardCommitmentBatchNumber})` : ""}
              </Typography>
            </Grid>
          )}
          <Grid item xs={fieldXs}>
            <TextField
              disabled={mode === "review" || isView}
              variant="outlined"
              size="small"
              type="text"
              value={state.ForwardCommitmentBatchNumber === undefined ? (alwaysShowDateFields ? "x" : "") : state.ForwardCommitmentBatchNumber}
              onChange={(e) => setState({ ...state, ForwardCommitmentBatchNumber: e.target.value })}
              fullWidth
            />
          </Grid>
        </>
      )}
    </SectionAccordion>
  );
}
